import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { HttpUtils } from 'app/shared/http-utils';
import { catchError, map, Observable, tap } from 'rxjs';
import {
  IContact,
  IContactResponse,
  IFriend, IFriendPreferences,
  IFriendsResponse,
  IFriendStatus,
  IRequestsResponse,
} from '../model/friends.model';
import { FacetsResponse } from '../../my-history/facet/facets-response';
import { FacetModel } from '../../my-history/facet/facet.model';
import { FacetUtils } from '../../my-history/utils/facet-utils';
import { ErrorHandlerService } from '../../shared/error-handler.service';
import { FacetAliasId, FacetId } from '../../my-history/facet/facet';
import { FacetFilterService } from '../../my-history/facet/facet-filter.service';
import { StoryStatus } from '../../my-history/story/story-status';
import { IStory } from '../../my-history/story/story';

@Injectable({
  providedIn: 'root',
})
export class FriendsCrudService {
  private facetFilter = inject(FacetFilterService);
  private http = inject(HttpClient);
  private apiUrl = HttpUtils.getApiUrl('/followers/v1/friends');
  private userApiUrl = HttpUtils.getApiUrl('/followers/v1/user');
  private unitUrl = HttpUtils.getApiUrl('/lenses/v1/units');
  private storiesApiUrl: string = HttpUtils.getApiUrl('/story/v1/stories');
  private errorService = inject(ErrorHandlerService);

  friendsTotal = signal(0);
  requestTotal = signal(0);
  contactsTotal = signal(0);

  friendFacets = signal<FacetModel[]>([]);

  getFriendsOfFriend(
    friendId: number,
    searchRequest?: string,
    take: number = 20,
    skip: number = 0,
    status?: IFriendStatus,
  ): Observable<IFriendsResponse> {
    let params = new HttpParams()
      .set('followerId', friendId)
      .set('take', take)
      .set('skip', skip)
      .set('order', 'full_name')
      .set('asc', true);

    if (searchRequest?.length) {
      params = params.set('search', searchRequest);
    }

    if (status) {
      params = params.set('status', status);
    }

    return this.http.get<IFriendsResponse>(this.apiUrl + '/' + friendId + '/friends', { params });
  }

  getFriends(
    searchRequest?: string,
    take: number = 20,
    skip: number = 0,
    status?: IFriendStatus,
  ): Observable<IFriendsResponse> {
    let params = new HttpParams().set('take', take).set('skip', skip).set('order', 'full_name').set('asc', true);

    if (searchRequest?.length) {
      params = params.set('search', searchRequest);
    }

    if (status) {
      params = params.set('status', status);
    }

    return this.http.get<IFriendsResponse>(this.apiUrl, { params });
  }

  setFriendsTotal(total: number) {
    this.friendsTotal.set(total);
  }

  getFriendsTotal() {
    return this.friendsTotal();
  }

  setRequestTotal(total: number) {
    this.requestTotal.set(total);
  }

  getRequestTotal() {
    return this.requestTotal();
  }

  setContactsTotal(total: number) {
    this.contactsTotal.set(total);
  }

  getContactsTotal() {
    return this.contactsTotal();
  }

  getUserById(userId: number): Observable<IFriend> {
    let params = new HttpParams();

    if (userId) {
      params = params.set('userId', userId);
    }

    return this.http.get<IFriend>(this.userApiUrl + '/' + userId.toString(), { params });
  }

  getFriendById(followerId: number): Observable<IFriend> {
    return this.http.get<IFriend>(this.apiUrl + '/' + followerId.toString());
  }

  getFriendsById(id: number, take: number = 20, skip: number = 0): Observable<IFriendsResponse> {
    let params = new HttpParams().set('take', take).set('skip', skip).set('asc', true).set('order', 'full_name');
    if (id) {
      params = params.set('friendId', id);
    }

    return this.http.get<IFriendsResponse>(this.apiUrl, { params });
  }

  // Оновлений метод getAllFriendStories з підтримкою пагінації
  getAllFriendStories(
    userId: number,
    start: string,
    end: string,
    page: number = 0,
    limit: number = 20
  ): Observable<FacetsResponse<IStory>> {
    // Розраховуємо skip на основі page та limit
    const skip = page * limit;

    let params = new HttpParams()
      .set('take', limit)
      .set('skip', skip)
      .set('asc', true)
      .set('order', 'start')
      .set('status', StoryStatus.publish);

    if (start) {
      params = params.set('start', start);
    }

    if (end) {
      params = params.set('end', end);
    }

    if (userId) {
      params = params.set('userId', userId);
    }

    return this.http
      .get<FacetsResponse<IStory>>(`${this.storiesApiUrl}/`, { params: params })
      .pipe(catchError((err) => this.errorService.handle(err)));
  }

  getStoriesForFacet(
    userId: number,
    selectedFacetId: string,
    start: string,
    end: string | undefined,
    take = 50,
  ): Observable<FacetsResponse<IStory>> {
    let params = new HttpParams()
      .set('take', take)
      .set('asc', true)
      .set('order', 'start')
      .set('status', StoryStatus.publish);

    if (start) {
      params = params.set('start', start);
    }

    if (end) {
      params = params.set('end', end);
    }

    if (userId) {
      params = params.set('userId', userId);
    }

    if (selectedFacetId) {
      params = params.set('unitId', selectedFacetId);
    }

    return this.http
      .get<FacetsResponse<IStory>>(`${this.storiesApiUrl}`, { params: params })
      .pipe(catchError((err) => this.errorService.handle(err)));
  }

  getChapterFacets(userId: number, startDate?: string, endDate?: string) {
    const start = startDate ? startDate : this.facetFilter.startDate();
    const end = endDate ? endDate : this.facetFilter.endDate();
    const params = new HttpParams()
      .set('take', 50)
      .set('excludedLenses', FacetId.chapter)
      .set('start', start)
      .set('end', end)
      .set('userId', userId);
    return this.http.get<FacetsResponse<FacetModel>>(`${this.unitUrl}`, { params }).pipe(
      map((response) => response.data),
      map((chapters) => FacetUtils.hronologicalSort(chapters)),
      tap((chapterFacets) => this.friendFacets.set(chapterFacets)),
      catchError((err) => this.errorService.handle(err)),
    );
  }

  removeFromFriends(friendId: string) {
    return this.http.delete(this.apiUrl + '/' + friendId);
  }

  addFriend(friendId: string) {
    const params = new HttpParams().set('followerId', friendId);

    return this.http.post(`${this.apiUrl}/${friendId}`, { params });
  }

  shareTo(friendId: string) {
    const params = new HttpParams().set('followerId', friendId);

    return this.http.post(`${this.apiUrl}/${friendId}/share-to`, { params });
  }

  getFriendFacets(userId: string, lenseId: number = FacetAliasId.chapter, start?: string | null, end?: string | null) {
    let params = new HttpParams().set('userId', userId).set('lenseId', lenseId);

    if (start) {
      params = params.set('start', start);
    }

    if (end) {
      params = params.set('end', end);
    }

    return this.http.get<FacetsResponse<FacetModel>>(`${HttpUtils.getApiUrl('/lenses/v1/units')}`, { params }).pipe(
      map((response) => response.data),
      map((chapters) => FacetUtils.hronologicalSort(chapters)),
      catchError((err) => this.errorService.handle(err)),
    );
  }

  stopShareFrom(followerId: string) {
    const params = new HttpParams().set('followerId', followerId);

    return this.http.post(`${this.apiUrl}/${followerId}/stop-share-from`, { params });
  }

  unfollowUser(followerId: string) {
    const params = new HttpParams().set('followerId', followerId);

    return this.http.put(`${this.apiUrl}/${followerId}/set-inactive`, { params });
  }

  getRequests(
    take: number = 20,
    skip: number = 0,
    status?: IFriendStatus,
    searchRequest?: string,
  ): Observable<IRequestsResponse> {
    let params = new HttpParams().set('take', take).set('skip', skip).set('asc', true).set('order', 'last_interaction');

    if (status) {
      params = params.set('status', status);
    }

    if (searchRequest) {
      params = params.set('search', searchRequest);
    }

    return this.http.get<IRequestsResponse>(`${this.apiUrl}/requests`, { params });
  }

  isUserFriend(userId: number) {
    const params = new HttpParams().set('followerId', userId);

    return this.http.get<IFriendsResponse>(`${this.apiUrl}/${userId}/is-friend`, { params });
  }

  getUsers(query = '', take = 10, skip = 0, isFriend = false): Observable<IFriendsResponse> {
    const params = new HttpParams().set('take', take).set('skip', skip).set('isFriend', isFriend).set('search', query);

    return this.http.get<IFriendsResponse>(HttpUtils.getApiUrl('/followers/v1/user'), { params });
  }

  getContacts(query = '', take = 10, skip = 0): Observable<IContactResponse> {
    let params = new HttpParams().set('take', take).set('skip', skip).set('asc', true).set('order', 'fullName');

    if (query) {
      params = params.set('search', query);
    }

    return this.http.get<IContactResponse>(HttpUtils.getApiUrl('/followers/v1/contacts'), { params });
  }

  updateUserContact(contact: IContact): Observable<IContactResponse> {
    return this.http.post<IContactResponse>(HttpUtils.getApiUrl('/followers/v1/contacts'), {
      fullName: contact.fullName,
      phone: contact.phone,
      email: contact.email,
    });
  }

  sendUserInvite(contactId: string) {
    const params = new HttpParams().set('contactId', contactId);

    return this.http.post<IContactResponse>(HttpUtils.getApiUrl('/followers/v1/contacts/' + contactId + '/invite'), {
      params,
    });
  }

  removeUserContact(contactId: string) {
    const params = new HttpParams().set('contactId', contactId);

    return this.http.delete<IContactResponse>(HttpUtils.getApiUrl('/followers/v1/contacts/' + contactId), { params });
  }

  getStoriesForFacetWithPagination(
    userId: number,
    selectedFacetId: string,
    start: string,
    end: string | undefined,
    take = 50,
    skip = 0
  ): Observable<FacetsResponse<IStory>> {
    let params = new HttpParams()
      .set('take', take)
      .set('skip', skip)
      .set('asc', true)
      .set('order', 'start')
      .set('status', StoryStatus.publish);

    if (start) {
      params = params.set('start', start);
    }

    if (end) {
      params = params.set('end', end);
    }

    if (userId) {
      params = params.set('userId', userId);
    }

    if (selectedFacetId) {
      params = params.set('unitId', selectedFacetId);
    }

    return this.http
      .get<FacetsResponse<IStory>>(`${this.storiesApiUrl}`, { params: params })
      .pipe(catchError((err) => this.errorService.handle(err)));
  }

  setFriendPreferencesBatch(usersIds: number[], preferences: {
    feedFavorite?: boolean,
    feedSnoozed?: boolean,
  }) {

    return this.http.patch(this.apiUrl + '/preferences/batch', {
      usersIds,
      preferences,
    });
  }
}
